// @ts-strict-ignore
import { Action, createReducer, on } from '@ngrx/store';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { VehicleGroup } from '@fc-core';
import { changeSortByListOrder } from '@fc-shared/utils/change-sort-by-list-order';
import { SortBy } from '@fc-shared/ui/sort-by/sort-by';

import * as VehicleGroupActions from '../actions/vehicle-group.actions';

export const vehicleGroupsFeatureKey = 'core-vehicle-groups';

export interface State extends EntityState<VehicleGroup> {
  vehicleGroupFilterId: number;
  selectedGroupId: number;
  defaultColors: string[];
  loading: boolean;
  error: any;
  searchText: string;
  sortBy: SortBy;
  sortByList: SortBy[];
}

export const adapter: EntityAdapter<VehicleGroup> =
  createEntityAdapter<VehicleGroup>();

export const initialState: State = adapter.getInitialState({
  vehicleGroupFilterId: -1,
  selectedGroupId: null,
  defaultColors: null,
  loading: false,
  error: null,
  searchText: '',
  sortBy: { name: 'Name', value: 'name', order: true },
  sortByList: [
    { name: 'Name', value: 'name', order: true },
    { name: 'Number of vehicles', value: 'numbers', order: null },
  ],
});

const vehicleGroupReducer = createReducer(
  initialState,
  on(
    VehicleGroupActions.addVehicleGroup,
    VehicleGroupActions.updateVehicleGroup,
    VehicleGroupActions.deleteVehicleGroup,
    VehicleGroupActions.loadVehicleGroups,
    VehicleGroupActions.removeVehicleFromGroup,
    VehicleGroupActions.addVehiclesToGroup,
    (state) => ({ ...state, loading: true }),
  ),
  on(VehicleGroupActions.resetDefaultVehicleGroupFilters, (state) => ({
    ...state,
    searchText: initialState.searchText,
    sortBy: initialState.sortBy,
    sortByList: initialState.sortByList,
  })),
  on(VehicleGroupActions.addVehicleGroupSuccess, (state, action) => ({
    ...adapter.addOne(action.vehicleGroup, state),
    loading: false,
  })),
  on(
    VehicleGroupActions.setVehicleGroupFilterId,
    (state, { vehicleGroupFilterId }) => ({
      ...state,
      vehicleGroupFilterId,
    }),
  ),
  on(VehicleGroupActions.setVehicleGroupSortBy, (state, action) => ({
    ...state,
    sortBy: {
      ...action.sortBy,
      order: action.sortBy === null ? true : !action.sortBy.order,
    },
    sortByList: changeSortByListOrder(state.sortByList, action.sortBy),
  })),
  on(VehicleGroupActions.setSearchVehicleGroupText, (state, action) => ({
    ...state,
    searchText: action.searchText,
  })),
  on(VehicleGroupActions.selectVehicleGroupId, (state, { id }) => ({
    ...state,
    selectedGroupId: id,
  })),
  on(
    VehicleGroupActions.getVehicleGroupDefaultColorsSuccess,
    (state, action) => ({
      ...state,
      defaultColors: action.colors,
    }),
  ),
  on(VehicleGroupActions.vehicleGroupsError, (state, action) => ({
    ...state,
    error: action.error,
    loading: false,
  })),
  on(VehicleGroupActions.updateCreatedVehicleGroup, (state, action) => {
    const changes = action.vehicle.groups?.map((group) => ({
      id: group,
      changes: {
        vehicles: [...state.entities[group].vehicles, action.vehicle.id],
      },
    }));
    return adapter.updateMany(changes, state);
  }),

  on(VehicleGroupActions.updateVehicleGroupSuccess, (state, action) => ({
    ...adapter.updateOne(action.vehicleGroup, state),
    loading: false,
  })),

  on(VehicleGroupActions.deleteVehicleGroupSuccess, (state, action) => ({
    ...adapter.removeOne(action.id, state),
    loading: false,
  })),

  on(VehicleGroupActions.loadVehicleGroupsSuccess, (state, action) => ({
    ...adapter.setAll(action.vehicleGroups, state),
    loading: false,
  })),
  on(VehicleGroupActions.removeVehicleFromGroupSuccess, (state, action) => ({
    ...adapter.setOne(action.vehicleGroup, state),
    loading: false,
  })),
  on(
    VehicleGroupActions.toggleVehicleInGroups,
    (state, { vehicle, groupsIds }) => {
      const groups = adapter.getSelectors().selectAll(state);
      const changes = groups.map((group) => ({
        id: group.id,
        changes: {
          vehicles: toggleVehiclesInGroups(group, groupsIds, vehicle),
        },
      }));
      return {
        ...adapter.updateMany(changes, state),
        loading: false,
      };
    },
  ),
  on(VehicleGroupActions.addVehicleToGroupSuccess, (state, action) => ({
    ...adapter.setOne(action.vehicleGroup, state),
    loading: false,
  })),
  on(VehicleGroupActions.addVehiclesToGroupSuccess, (state, action) => ({
    ...adapter.setOne(action.vehicleGroup, state),
    loading: false,
  })),
);

export function reducer(state: State | undefined, action: Action) {
  return vehicleGroupReducer(state, action);
}

export const { selectIds, selectEntities, selectAll, selectTotal } =
  adapter.getSelectors();

export const getVehicleGroupFilterId = (state: State) =>
  state.vehicleGroupFilterId;
export const getSelectedGroupId = (state: State) => state.selectedGroupId;
export const getDefaultColors = (state: State) => state.defaultColors;
export const getGroupsError = (state: State) => state.error;
export const getGroupsLoading = (state: State) => state.loading;
export const getGroupSearchText = (state: State) => state.searchText;
export const getVehicleGroupSortBy = (state: State) => state.sortBy;
export const getVehicleGroupSortByList = (state: State) => state.sortByList;

const toggleVehiclesInGroups = (
  group: VehicleGroup,
  groupsIds: number[],
  vehicle: number,
) => {
  if (groupsIds.length === 0 || !groupsIds.includes(group.id)) {
    return group.vehicles.filter((i) => i !== vehicle);
  } else if (groupsIds.includes(group.id)) {
    return [...group.vehicles, vehicle];
  }
};
